import { Dialog, Inset, Text } from '@radix-ui/themes';
import { Command } from 'cmdk';
import mixpanel from 'mixpanel-browser';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Icon from '~/components/Icon';
import Spinner from '~/components/Spinner';
import Muted from '~/components/Typography/Muted';
import useAuthentication from '~/hooks/use-authentication';
import useClipboard from '~/hooks/use-clipboard';
import useNotification from '~/hooks/use-notification';
import useOrganizations from '~/hooks/use-organizations';
import useSims from '~/hooks/use-sims';

const ALLOWED_LABEL_LENGTH = 10;

const CommandRoot = styled(Command)`
  width: 100%;
  overflow: hidden;
  padding: 0;
`;

const Input = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  column-gap: 0.5rem;
  padding-left: 0.75rem;
  border-bottom: 1px solid var(--neutral-4);
`;

const CommandInput = styled(Command.Input)`
  flex: 1;
  padding-top: 1rem;
  padding-bottom: 1rem;
  border: none;
  outline: none;
  background-color: var(--solid-panel-color);
`;

const CommandGroup = styled(Command.Group)`
  [cmdk-group-heading] {
    color: var(--neutral-10);
    padding: 0 0.875rem 0.25rem;
  }
`;

const CommandList = styled(Command.List)`
  height: min(300px, var(--cmdk-list-height));
  max-height: 450px;
  padding: 0.5rem;
  overflow: auto;
  overscroll-behavior: contain;
  transition: 100ms ease;
  transition-property: height;
  box-sizing: content-box;
`;

const CommandItem = styled(Command.Item)`
  cursor: pointer;
  display: flex;
  padding: 0.625rem 0.875rem;
  justify-content: space-between;
  align-items: center;
  position: relative;
  content-visibility: auto;
  border-radius: 0.375rem;

  &[data-selected='true'] {
    background-color: var(--neutral-a3);
  }
`;

const CommandEmpty = styled(Command.Empty)`
  padding: 0 0.875rem;
`;

const CommandMenu = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const { isGod, isDemiGod, assumeOrganizationId, assumedOrganizationId } = useAuthentication();
  const clipboard = useClipboard();
  const { addNotification } = useNotification();
  const { data: simsSearchData, search: simsSearch } = useSims({
    disableFetchSimsOnChange: true,
    disableSetUrlParamsOnSearch: true,
    from: 'CommandMenu',
    ignoreQueryParams: true,
  });
  const organizations = useOrganizations();
  const history = useHistory();

  function closeMenu() {
    setIsOpen(false);
    setPage(null);
    setSearch('');
    simsSearch.clear();
  }

  function navigate(path) {
    history.push(path);
    mixpanel.track('Click command palette action', { path });
    closeMenu();
  }

  useEffect(() => {
    function down(e) {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen(open => {
          if (!open) mixpanel.track('Command palette opened');
          return !open;
        });
      }
    }

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const defaultItems = [
    {
      id: 'dashboard',
      children: 'Go to Dashboard',
      onClick: () => {
        navigate('/');
      },
    },
    {
      id: 'sims',
      children: 'Go to SIMs',
      onClick: () => {
        navigate('/sims');
      },
    },
    {
      id: 'usage',
      children: 'Go to Usage',
      onClick: () => {
        navigate('/usage');
      },
    },
    {
      id: 'connectors',
      children: 'Go to Connectors',
      onClick: () => {
        navigate('/connectors');
      },
    },
    {
      id: 'network-lists',
      children: 'Go to Network Lists',
      onClick: () => {
        navigate('/network-lists');
      },
    },
    {
      id: 'webhooks',
      children: 'Go to Webhooks',
      onClick: () => {
        navigate('/webhooks');
      },
    },
    {
      id: 'api-keys',
      children: 'Go to API Keys',
      onClick: () => {
        navigate('/api-keys');
      },
    },
    {
      id: 'tags',
      children: 'Go to Tags',
      onClick: () => {
        navigate('/tags');
      },
    },
    {
      id: 'users',
      children: 'Go to Users',
      onClick: () => {
        navigate('/users');
      },
    },
    {
      id: 'error-log',
      children: 'Go to Error Log',
      onClick: () => {
        navigate('/error-log');
      },
    },
    {
      id: 'audit-log',
      children: 'Go to Audit Log',
      onClick: () => {
        navigate('/audit-logs');
      },
    },
  ];

  if (isGod || isDemiGod) {
    defaultItems.push(
      {
        id: 'organizations',
        children: 'Go to Organizations',
        onClick: () => {
          navigate('/admin/organizations');
        },
      },
      {
        id: 'price-lists',
        children: 'Go to Pricelists',
        onClick: () => {
          navigate('/admin/pricelists');
        },
      },
      {
        id: 'switch-organization',
        children: 'Switch organization',
        closeOnSelect: false,
        onClick: () => {
          setPage('organizations');
          setSearch('');
        },
      },
    );

    if (assumedOrganizationId) {
      defaultItems.push({
        id: 'copy-url-with-organization-id',
        children: 'Copy URL with organization ID',
        onClick: () => {
          if (!clipboard.canWrite) return;

          const url = new URL(window.location.href);
          url.searchParams.set('org_id', assumedOrganizationId);
          clipboard.write(url.toString());
          addNotification({
            message: 'URL copied to clipboard',
            type: 'info',
          });
          closeMenu();
        },
      });
    }
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={value => {
        setIsOpen(value);
        setSearch('');
        setPage(null);
        simsSearch.clear();
      }}
    >
      <Dialog.Content aria-label='Command Menu'>
        <Inset>
          <CommandRoot
            loop
            onKeyDown={e => {
              const isEscape = e.key === 'Escape';
              const isBackspace = e.key === 'Backspace';

              const shouldClose = isEscape && !page && !search;
              const shouldClearSearch = isEscape && search;
              const shouldUnsetPage = isEscape || (isBackspace && !search);

              if (shouldClose) {
                e.preventDefault();
                setIsOpen(false);
              }

              if (shouldClearSearch) {
                e.preventDefault();
                simsSearch.clear();
                setSearch('');
              }

              if (shouldUnsetPage) {
                e.preventDefault();
                setPage(null);
              }
            }}
          >
            <Input>
              {simsSearch.loading ? (
                <Spinner size={16} />
              ) : (
                <Icon
                  name='search-dark-medium'
                  height={16}
                  width={16}
                  inline
                />
              )}
              <CommandInput
                placeholder='Search'
                value={search}
                onValueChange={value => {
                  setSearch(value);
                  const isSearchEmptyString = value === '';
                  if (isSearchEmptyString) {
                    simsSearch.clear();
                    return;
                  }
                  simsSearch.onChange({ target: { value } });
                }}
              />
            </Input>
            <CommandList>
              {!page && (
                <>
                  <CommandGroup heading='Navigation'>
                    {defaultItems.map(({ id, children, onClick }) => (
                      <CommandItem
                        key={id}
                        value={children}
                        onSelect={onClick}
                      >
                        {children}
                      </CommandItem>
                    ))}
                  </CommandGroup>

                  {simsSearchData?.length > 0 && (
                    <CommandGroup heading='SIMs'>
                      {simsSearchData?.map(sim => {
                        const escapedLabel = sim.label.replace(/["'`]/g, '\\$&');
                        return (
                          <CommandItem
                            key={sim.id}
                            value={`${sim.id} ${escapedLabel} ${sim.ipv4} ${sim.iccid}`} // Value is used for fuzzy search hence the weird string
                            onSelect={() => {
                              mixpanel.track('Go to SIM from command palette', { simId: sim.id });
                              navigate(`/sims/${sim.id}`);
                            }}
                          >
                            {sim.id}
                            <Text>
                              {`${
                                sim.label?.length > 0
                                  ? `Label → "${
                                      sim.label?.length <= ALLOWED_LABEL_LENGTH
                                        ? sim.label
                                        : `${sim.label.substring(0, ALLOWED_LABEL_LENGTH - 3)}...`
                                    }" `
                                  : ''
                              }IP → ${sim.ipv4}`}
                            </Text>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  )}
                </>
              )}
              {page === 'organizations' && (
                <CommandGroup heading='Organizations'>
                  {organizations.data.map(org => (
                    <CommandItem
                      key={org.id}
                      value={org.entity}
                      onSelect={() => {
                        assumeOrganizationId(org.id);
                        setPage(null);
                        setSearch('');
                        setIsOpen(false);
                      }}
                    >
                      {org.entity}
                      <Muted>{`ID → "${org.id}"`}</Muted>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              <CommandEmpty>No results found.</CommandEmpty>
            </CommandList>
          </CommandRoot>
        </Inset>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default CommandMenu;
